import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Arctic.css';

/* ── helpers ────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

/* ── useReveal ──────────────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.arc-reveal, .arc-reveal-left, .arc-reveal-right, .arc-reveal-scale';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('arc-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('arc-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ── Countdown ──────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms % 86400000 / 3600000),
        m: Math.floor(ms % 3600000 / 60000),
        s: Math.floor(ms % 60000 / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);
  return (
    <div className="arc-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="arc-cu">
          <span className="arc-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="arc-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── CalendarWidget ─────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ n: i, cur: true });
  while (cells.length < 42) cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  return (
    <div className="arc-calendar">
      <div className="arc-cal-header">
        <span style={{ color: accent, fontWeight: 600 }}>{MON[mo]}</span>
        <span className="arc-cal-yr">{yr}</span>
      </div>
      <div className="arc-cal-grid">
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
          <div key={h} className="arc-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isDay = c.cur && c.n === day;
          return (
            <div key={i}
              className={`arc-cal-cell${!c.cur ? ' arc-cal-muted' : ''}${isDay ? ' arc-cal-today' : ''}`}
              style={isDay ? { background: accent, color: '#fff' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVPForm ───────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus]   = useState('confirmed');
  const [name, setName]       = useState('');
  const [survey, setSurvey]   = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone]       = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="arc-rsvp-done" style={{ borderColor: accent }}>
      <div className="arc-rsvp-done-icon" style={{ color: accent }}>✓</div>
      <strong style={{ color: accent }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="arc-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="arc-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`arc-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#fff', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="arc-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="arc-survey-q">
          <p className="arc-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="arc-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="arc-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`arc-rsvp-opt arc-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { background: accent, color: '#fff', borderColor: accent } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={selected}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="arc-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`arc-rsvp-opt arc-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#fff', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p style={{ fontSize: '0.82rem', color: '#7a8a9a', marginBottom: 4 }}>{bc.message.fieldLabel}</p>
          )}
          <textarea className="arc-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}
      <button type="submit" className="arc-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ───────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="arc-wishlist">
      {items.map(item => (
        <div key={item.id} className="arc-wish-item">
          <div>
            <div className="arc-wish-name">{item.name}</div>
            {item.price && <div className="arc-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="arc-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="arc-btn-outline">
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="arc-btn" style={{ background: '#7a8a9a' }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="arc-btn" style={{ background: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Section heading with blue left rule ────────────────────── */
function SectionTitle({ children, accent }) {
  return (
    <div className="arc-section-title-wrap">
      <div className="arc-section-rule" style={{ background: accent }} />
      <h2 className="arc-section-title">{children}</h2>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN — Arctic
═══════════════════════════════════════════════════════════════ */
export default function Arctic({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || design?.config?.primaryColor || '#3a7bd5';

  const fontTitle = project.fonts?.title || design?.config?.fontTitle || null;
  const fontBody  = project.fonts?.body  || design?.config?.fontBody  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-disp': `'${fontTitle}', 'Space Grotesk', sans-serif` } : {}),
    ...(fontBody  ? { '--f-body': `'${fontBody}', 'Inter', sans-serif` }         : {}),
  };

  /* Dynamic Google Fonts */
  useEffect(() => {
    const id = 'arc-base-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Inter:wght@300;400;500&display=swap';
      document.head.appendChild(link);
    }
    const families = [fontTitle, fontBody].filter(Boolean);
    if (!families.length) return;
    const uid = 'arc-dynamic-fonts';
    let link = document.getElementById(uid);
    if (!link) { link = document.createElement('link'); link.id = uid; link.rel = 'stylesheet'; document.head.appendChild(link); }
    const query = families.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const p1        = photos[0];
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride  = project.bride || 'Невеста';
  const groom  = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_MENU      = blocks.includes('menu');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  return (
    <div className="arc-page" ref={pageRef} style={fontStyle} container-name="arctic" container-type="inline-size">

      {/* ══ NAV ══════════════════════════════════════════════════ */}
      {HAS_MENU && (
        <nav className="arc-nav">
          <span className="arc-nav-brand" style={{ color: accent }}>{bride} &amp; {groom}</span>
          <div className="arc-nav-links">
            {HAS_SCHEDULE  && <a href="#schedule"  className="arc-nav-a">Тайминг</a>}
            {HAS_MAP       && <a href="#map"       className="arc-nav-a">Локация</a>}
            {HAS_DRESSCODE && <a href="#dresscode" className="arc-nav-a">Дресс-код</a>}
            {HAS_RSVP      && <a href="#rsvp"      className="arc-nav-a" style={{ color: accent }}>Анкета</a>}
          </div>
        </nav>
      )}

      {/* ══ HERO (split layout) ══════════════════════════════════ */}
      <section className="arc-hero">
        {/* Left — photo with geometric frame */}
        <div className="arc-hero-photo-wrap">
          {p1
            ? <img className="arc-hero-img" src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
            : <div className="arc-hero-img arc-hero-img-empty" />
          }
          {/* Geometric frame overlay — SVG gap-corner rectangle */}
          <svg className="arc-hero-frame" viewBox="0 0 100 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="0"   y1="8"   x2="0"   y2="0"   stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="0"   y1="0"   x2="8"   y2="0"   stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="92"  y1="0"   x2="100" y2="0"   stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="100" y1="0"   x2="100" y2="8"   stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="100" y1="92"  x2="100" y2="100" stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="100" y1="100" x2="92"  y2="100" stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="8"   y1="100" x2="0"   y2="100" stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
            <line x1="0"   y1="100" x2="0"   y2="92"  stroke={accent} strokeWidth="0.8" vectorEffect="non-scaling-stroke"/>
          </svg>
        </div>

        {/* Right — names panel */}
        <div className="arc-hero-panel">
          <div className="arc-hero-content arc-reveal-left">
            <div className="arc-hero-date" style={{ color: accent }}>{fmtDate(weddingDate)}</div>
            <div className="arc-hero-rule" style={{ background: accent }} />
            <div className="arc-hero-names">
              <span className="arc-hero-name">{bride}</span>
              <span className="arc-hero-amp" style={{ color: accent }}>&amp;</span>
              <span className="arc-hero-name">{groom}</span>
            </div>
            <div className="arc-hero-rule" style={{ background: accent }} />
            {(bc.hero?.subtitle || project.wedding_slogan) && (
              <p className="arc-hero-slogan">{bc.hero?.subtitle || project.wedding_slogan}</p>
            )}
            {project.wedding_time && (
              <div className="arc-hero-time" style={{ color: accent }}>{project.wedding_time}</div>
            )}
            {HAS_RSVP && (
              <a href="#rsvp" className="arc-btn arc-hero-btn" style={{ background: accent }}>
                {bc.rsvp?.yesText || 'Заполнить анкету'} →
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ══ INVITATION ═══════════════════════════════════════════ */}
      <section className="arc-section arc-invitation" id="invitation">
        <div className="arc-inv-left arc-reveal-left">
          <SectionTitle accent={accent}>
            {bc.message?.title || 'Дорогие наши!'}
          </SectionTitle>
          <div className="arc-body-text">
            {bc.message?.text
              ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
              : (
                <>
                  <p>С огромным волнением и радостью хотим <strong>пригласить вас</strong> на торжество в честь нашего <strong>бракосочетания</strong>!</p>
                  <p>В этот особенный день мы будем счастливы видеть каждого из вас рядом — разделить радость и создать воспоминания навсегда.</p>
                  <p>Пожалуйста, подтвердите присутствие через форму ниже.</p>
                </>
              )
            }
          </div>
          {HAS_RSVP && (
            <a href="#rsvp" className="arc-btn" style={{ background: accent, marginTop: 8 }}>
              {bc.rsvp?.btnText || 'Заполнить анкету'}
            </a>
          )}
        </div>

        <div className="arc-inv-right arc-reveal-right">
          {/* Photos */}
          <div className="arc-inv-photos">
            {invP1 ? (
              <div className="arc-inv-photo arc-inv-photo--a">
                <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
              </div>
            ) : (
              <div className="arc-inv-photo arc-inv-photo--a arc-inv-photo--empty">
                <span>Ваше фото</span>
              </div>
            )}
            {invP2 ? (
              <div className="arc-inv-photo arc-inv-photo--b">
                <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
              </div>
            ) : (
              <div className="arc-inv-photo arc-inv-photo--b arc-inv-photo--empty">
                <span>Второе фото</span>
              </div>
            )}
          </div>

          <CalendarWidget
            date={weddingDate}
            accent={accent}
            addBtn={HAS_CALENDAR && (() => {
              const d = new Date(weddingDate);
              const pad = n => String(n).padStart(2, '0');
              const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
              const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
              const href = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
              return (
                <a href={href} target="_blank" rel="noopener noreferrer"
                   className="arc-cal-add-btn" style={{ borderColor: accent, color: accent }}>
                  + {bc.calendar?.title || 'Добавить в календарь'}
                </a>
              );
            })()}
          />
        </div>
      </section>

      {/* ══ COUNTDOWN ════════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <section className="arc-section arc-section--blue" id="countdown">
          <div className="arc-section-inner">
            <div className="arc-reveal-left">
              <SectionTitle accent={accent}>{bc.countdown?.title || 'До торжества'}</SectionTitle>
              <BlockSubtitle text={bc.countdown?.subtitle} className="arc-muted-text" />
            </div>
            <div className="arc-reveal" style={{ '--d': '.15s' }}>
              <Countdown date={weddingDate} accent={accent} />
            </div>
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ═════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <section className="arc-section" id="schedule">
          <div className="arc-section-inner">
            <div className="arc-reveal-left">
              <SectionTitle accent={accent}>{bc.schedule?.title || 'Тайминг'}</SectionTitle>
              <BlockSubtitle text={bc.schedule?.subtitle} className="arc-muted-text" />
            </div>
            <div className="arc-schedule-list">
              {optional.length > 0 && (
                <div className="arc-sched-group">
                  <div className="arc-sched-group-label" style={{ color: accent }}>Необязательная часть</div>
                  {optional.map((item, i) => (
                    <div key={i} className="arc-sched-item arc-reveal" style={{ '--d': `${i * 0.08}s` }}>
                      <div className="arc-sched-time" style={{ color: accent }}>{item.time}</div>
                      <div className="arc-sched-dot" style={{ borderColor: accent }} />
                      <div className="arc-sched-info">
                        <div className="arc-sched-event">{item.event}</div>
                        {item.description && <div className="arc-sched-desc">{item.description}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="arc-sched-group">
                {optional.length > 0 && (
                  <div className="arc-sched-group-label" style={{ color: accent }}>Обязательная часть</div>
                )}
                {(allItems.length > 0 ? mainItems : [
                  { time: '14:00', event: 'Сбор гостей' },
                  { time: '15:00', event: 'Церемония' },
                  { time: '16:00', event: 'Фотосессия' },
                  { time: '17:00', event: 'Банкет' },
                ]).map((item, i) => (
                  <div key={i} className={`arc-sched-item arc-reveal${allItems.length === 0 ? ' arc-sched-placeholder' : ''}`}
                    style={{ '--d': `${i * 0.08}s` }}>
                    <div className="arc-sched-time" style={{ color: allItems.length > 0 ? accent : accent + '70' }}>{item.time}</div>
                    <div className="arc-sched-dot" style={{ borderColor: accent }} />
                    <div className="arc-sched-info">
                      <div className="arc-sched-event">{item.event}</div>
                      {item.description && <div className="arc-sched-desc">{item.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══ MAP ══════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <section className="arc-section arc-section--blue" id="map">
          <div className="arc-section-inner arc-section-inner--col">
            <div className="arc-reveal">
              <SectionTitle accent={accent}>{bc.map?.title || 'Локация'}</SectionTitle>
              <BlockSubtitle text={bc.map?.subtitle} className="arc-muted-text" />
            </div>
            <div className="arc-venues arc-reveal" style={{ '--d': '.1s' }}>
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="arc-venue-row">
                  <div className="arc-venue-pin" style={{ color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="arc-venue-info">
                    {v.label && <span className="arc-venue-label" style={{ color: accent }}>{v.label} · </span>}
                    {v.name  && <span className="arc-venue-name">{v.name} · </span>}
                    <span>{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer" className="arc-btn-outline" style={{ borderColor: accent, color: accent }}>
                    Маршрут →
                  </a>
                </div>
              )) : (
                project.wedding_address
                  ? <div className="arc-venue-row"><span>{project.wedding_address}</span></div>
                  : <div className="arc-muted-text">Адрес будет добавлен на шаге «Данные»</div>
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="arc-map-frame arc-reveal" style={{ '--d': '.2s' }}>
              {(() => {
                if (project.map_url) return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width:'100%', height:'100%', border:'none' }} />;
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const mapSrc = `https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=16&pt=${pts}&lang=ru_RU`;
                  return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width:'100%', height:'100%', border:'none' }} />;
                }
                const addr = venues.length > 0 ? venues.map(v => v.address).filter(Boolean).join(' — ') : (project.wedding_address || '');
                if (!addr) return <MapFrame region={project.region} title="Карта" src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU" allowFullScreen style={{ width:'100%', height:'100%', border:'none' }} />;
                const mapSrc = venues.length > 1
                  ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v => encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width:'100%', height:'100%', border:'none' }} />;
              })()}
            </div>
            )}
          </div>
        </section>
      )}

      {/* ══ DRESSCODE ════════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <section className="arc-section" id="dresscode">
          <div className="arc-section-inner arc-section-inner--col">
            <div className="arc-reveal">
              <SectionTitle accent={accent}>{bc.palette?.title || 'Дресс-код'}</SectionTitle>
              <BlockSubtitle text={bc.palette?.subtitle} className="arc-body-text" tag="div" />
            </div>
            <div className="arc-swatches arc-reveal" style={{ '--d': '.1s' }}>
              {(project.dresscode_palette || []).map((sw, i) => {
                const c = sw.color || '#ffffff';
                const hex = c.replace('#', '');
                const r = parseInt(hex.slice(0, 2), 16) || 255, g = parseInt(hex.slice(2, 4), 16) || 255, b = parseInt(hex.slice(4, 6), 16) || 255;
                const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                return (
                  <div key={i} className="arc-swatch arc-reveal-scale" style={{ background: c, border: isLight ? '1px solid #d0dae8' : 'none', '--d': `${i * 0.08}s` }}>
                    <span style={{ color: isLight ? '#0f1923' : '#fff' }}>{sw.label}</span>
                  </div>
                );
              })}
            </div>
            {dcPhotos.length > 0 && (
              <div className="arc-dc-photos arc-reveal" style={{ '--d': '.15s' }}>
                {dcPhotos.map((p, i) => (
                  <div key={i} className="arc-dc-photo">
                    <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ RSVP / WISHLIST ══════════════════════════════════════ */}
      {(HAS_RSVP || HAS_WISHLIST) && (
        <section className="arc-section arc-section--blue" id="rsvp">
          <div className="arc-section-inner arc-section-inner--col">
            {HAS_RSVP && (
              <div className="arc-card arc-reveal" style={{ '--d': '0s' }}>
                <SectionTitle accent={accent}>{bc.rsvp?.title || 'Анкета'}</SectionTitle>
                <BlockSubtitle text={bc.rsvp?.subtitle} className="arc-muted-text" />
                {bc.rsvp?.deadline && (
                  <p className="arc-muted-text" style={{ color: accent }}>
                    Ответьте до: <strong>{new Date(bc.rsvp.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  </p>
                )}
                <RSVPForm project={project} accent={accent} />
              </div>
            )}
            {HAS_WISHLIST && (
              <div className="arc-card arc-reveal" style={{ '--d': '.1s' }}>
                <SectionTitle accent={accent}>{bc.wishlist?.title || 'Что дарить'}</SectionTitle>
                <BlockSubtitle text={bc.wishlist?.subtitle} className="arc-muted-text" />
                <Wishlist project={project} accent={accent} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ WATERMARK ════════════════════════════════════════════ */}
      {(!project.payment_plan || project.payment_plan === 'free') && (
        <div style={{
          position: 'fixed', bottom: 0, right: 0, zIndex: 9999,
          background: 'rgba(15,25,35,0.85)', color: '#e8f0fb',
          padding: '7px 16px', fontSize: '0.78rem',
          borderTopLeftRadius: 8, backdropFilter: 'blur(6px)',
        }}>
          Создано на <strong style={{ color: '#3a7bd5' }}>
            <a href="/" style={{ color: '#3a7bd5', textDecoration: 'none' }}>Amorika</a>
          </strong>
        </div>
      )}

      {/* ══ FOOTER ═══════════════════════════════════════════════ */}
      <footer className="arc-footer" style={{ borderTopColor: accent }}>
        <div className="arc-footer-rule" style={{ background: accent }} />
        <div className="arc-footer-names" style={{ color: accent }}>
          {bride} &amp; {groom}
        </div>
        <div className="arc-footer-date">{fmtDate(weddingDate)}</div>
        {(bc.message?.text || project.wedding_slogan) && (
          <div className="arc-footer-slogan">{project.wedding_slogan}</div>
        )}
        <div className="arc-footer-brand">
          Создано на <a href="/" style={{ color: accent, textDecoration: 'none' }}><strong>Amorika</strong></a>
        </div>
      </footer>

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
